"use client";
import React, { useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import {
  CmsEvent,
  filterByDate,
  reduceByDate,
  EventCategory,
  filterEvents,
} from '@/lib/events';
import { fetchCMS } from '@/lib/cms';
import { renderDateItem, renderChip, renderFilterCard } from '@/components/Events';
import Styles from './events.module.scss';
import { SvgIcon } from '@/components/ui/SvgIcon';
import { formatDate } from '@/lib/utils';

type EventsClientProps = {
  categories?: EventCategory[];
  serverTime: string;
};

const getMonthRange = (monthKey: string) => {
  const [yearStr, monthStr] = monthKey.split('-');
  const year = parseInt(yearStr, 10);
  const month = parseInt(monthStr, 10);
  const start = new Date(Date.UTC(year, month - 1, 1, 0, 0, 0, 0)).toISOString();
  const end = new Date(Date.UTC(year, month, 1, 0, 0, 0, 0)).toISOString();
  return { start, end };
};

const toMonthKey = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

const monthKeyToDate = (key: string) => {
  const [y, m] = key.split('-');
  return new Date(+y, +m - 1, 1);
};

const shiftMonth = (monthKey: string, delta: number): string => {
  const [yearStr, monthStr] = monthKey.split('-');
  const year = parseInt(yearStr, 10);
  const month = parseInt(monthStr, 10) + delta;
  return toMonthKey(new Date(year, month - 1, 1));
};

const EventsClient = ({ categories, serverTime }: EventsClientProps) => {
  const [events, setEvents] = useState<CmsEvent[]>([]);
  const [selected, setSelected] = useState<string[]>(['ALL']);
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [clientTime, setClientTime] = useState<number | null>(null);

  const today = useMemo(() => (
    clientTime ? new Date(clientTime) : new Date(serverTime)
  ), [clientTime, serverTime]);

  const [selectedMonth, setSelectedMonth] = useState(() =>
    toMonthKey(today)
  );

  useEffect(() => {
    const now = new Date();
    setClientTime(now.getTime());
    setSelectedMonth(toMonthKey(now));
  }, []);

  useEffect(() => {
    if (clientTime === null) return;

    const { start, end } = getMonthRange(selectedMonth);

    (async () => {
      try {
        const { data } = await fetchCMS<CmsEvent[]>('events', {
          normalize: true,
          sort: "start",
          params: {start, end }});
        setEvents(data ?? []);
      } catch {
        setEvents([]);
      }
    })();
  }, [selectedMonth, clientTime]);

  const sortedEvents = useMemo(() => (
    events.slice().sort(filterByDate)
  ), [events]);

  const filtered = useMemo(() => (
    filterEvents(sortedEvents, selected, 0, [selectedMonth], today.getTime())
  ), [sortedEvents, selected, selectedMonth, today]);

  const grouped = useMemo(() => (
    filtered.reduce<Record<string, CmsEvent[]>>(reduceByDate, {})
  ), [filtered]);

  const dateKeys = Object.keys(grouped).sort();

  const onPrevMonthClick = () => setSelectedMonth(prev => shiftMonth(prev, -1));
  const onNextMonthClick = () => setSelectedMonth(prev => shiftMonth(prev, 1));

  const onToggleCategory = (name: string) => {
    setSelected(prev => {
      const hasAll = prev.includes('ALL');
      const isOn = prev.includes(name);
      if (hasAll) return isOn ? ['ALL'] : [name];
      if (isOn) {
        const next = prev.filter(p => p !== name);
        return next.length ? next : ['ALL'];
      }
      return [...prev, name];
    });
  };

  const onRemoveSelected = (name: string) => {
    setSelected(prev => {
      const next = prev.filter(p => p !== name);
      return next.length ? next : ['ALL'];
    });
  };

  if (clientTime === null) {
    return (
      <section className={Styles.eventsWrapper} data-transition="forward">
        <header className={`${Styles.header} fixed`}>
          <h1>Events</h1>
        </header>
      </section>
    );
  }

  return (
    <section className={Styles.eventsWrapper} data-transition="forward">
      <header className={`${Styles.header} fixed`}>
        <h1>Events</h1>
        <div className={Styles.filterBar}>

          <div className={Styles.monthSwitcher}>
            <button
              className={Styles.monthButton}
              onClick={onPrevMonthClick}
              aria-label="Previous month"
            >
              ◀
            </button>
            <div className={Styles.monthLabel} suppressHydrationWarning>
               {formatDate(monthKeyToDate(selectedMonth).toDateString(), "MMMM yyyy")}
            </div>
            <button
              className={Styles.monthButton}
              onClick={onNextMonthClick}
              aria-label="Next month"
            >▶</button>
          </div>

          <div className={Styles.filterArea}>
            <div className={Styles.chips}>
              {selected.filter(s => s !== 'ALL').map(renderChip(onRemoveSelected))}
            </div>

            <div className={cn(Styles.filterIconWrap, { [Styles.active]: overlayOpen })}>
              <div
                className={Styles.filterIcon}
                onClick={() => setOverlayOpen(v => !v)}
                aria-expanded={overlayOpen}
                aria-label="Open filters"
              >
                <SvgIcon size={24} iconName="selectCategories" />
              </div>
              <div className={Styles.filterOverlayWrapper} aria-hidden={!overlayOpen}>
                <ul
                  className={Styles.filterOverlay}
                  role="dialog"
                  aria-label="Category filters"
                >
                  {categories?.map(renderFilterCard(onToggleCategory, selected))}
                </ul>
              </div>
            </div>
          </div>
        </div>

      </header>

      <article className={Styles.eventsListWrapper}>
        {dateKeys.length
          ? (
            <ol className={Styles.eventsList}>
              {dateKeys.map(renderDateItem(grouped))}
            </ol>
          )
          : (
            <div className={Styles.noEvents}>
              <h2>Stay tuned</h2>
              <p>
                The stage is quiet for now, but not for long. Check back soon for upcoming live music, comedy, and events.
              </p>
            </div>
          )
        }
      </article>
    </section>
  );
};

export default EventsClient;
